import P from '@hero/ui-kit/typography/P';
import { MarkButton, MarkHeading } from '../../../components/MarkControls';
import React from 'react';
import * as styles from '../StepCanvas/journey.module.css';
import MarkModal from '../../../components/MarkModal';
import CheckSegmentForm from './forms/CheckSegmentForm';
import ActivityForm from './forms/ActivityForm';
import AbSplitForm from './forms/AbSplitForm';
import type { DripStep, CheckSegmentStep, ActivityStep, AbSplitStep } from '../../../types';
import type { GraphError } from '../../../hooks/useDripGraphValidation';
type Props = {
    step: DripStep;
    allSteps: DripStep[];
    entryStepId: string | null;
    onChange: (id: string, patch: Partial<DripStep>) => void;
    onSetEntry: (id: string) => void;
    entryError?: string | null;
    onDelete: (id: string) => void;
    onEditTemplate?: (stepId: string) => void;
    onSaveAndEditTemplate?: (stepId: string) => void;
    saveAndEditTemplateDisabled?: boolean;
    sendWindowErrors?: GraphError[];
    hasSavedTemplate?: boolean;
    canEditTemplate: boolean;
    persistedStepIds?: Set<string>;
    savedStep?: DripStep;
    dripTargetKind?: string;
    onClose: () => void;
};
const StepConfigPanel: React.FC<Props> = ({
    step,
    allSteps,
    entryStepId,
    onChange,
    onSetEntry,
    entryError,
    onDelete,
    onEditTemplate,
    onSaveAndEditTemplate,
    saveAndEditTemplateDisabled,
    sendWindowErrors,
    hasSavedTemplate,
    canEditTemplate,
    persistedStepIds,
    savedStep,
    dripTargetKind,
    onClose
}) => {
    const isEntry = entryStepId === step.id;
    // The server's template row is tagged with the saved channel, so a
    // changed-but-unsaved channel must be saved before its template opens.
    const unsavedChannelChange =
        step.type === 'activity' &&
        savedStep?.type === 'activity' &&
        savedStep.activity !== step.activity;
    const typeLabel =
        step.type === 'check_segment'
            ? 'Check segment'
            : step.type === 'activity'
              ? step.activity === 'send_email'
                  ? 'Send email'
                  : step.activity === 'send_sms'
                    ? 'Send SMS'
                    : 'External action'
              : 'A/B split';
    return (
        <MarkModal
            onClose={onClose}
            aria-label={`Configure step ${step.id}`}
            styles={{
                width: 'min(64rem, 90vw)'
            }}
        >
            <div className={styles.inspectorHeader}>
                <MarkHeading role="h5" tabIndex={-1} style={{ margin: 0 }}>
                    {typeLabel}
                </MarkHeading>
                <span className={styles.stepId} title={step.id}>
                    {step.id}
                </span>
            </div>
            {!isEntry && (
                <MarkButton
                    compact
                    type="button"
                    onClick={() => onSetEntry(step.id)}
                    style={{
                        marginBottom: '1rem'
                    }}
                >
                    Set as entry step
                </MarkButton>
            )}
            {entryError && (
                <P
                    role="alert"
                    styles={{
                        color: 'var(--mark-danger)',
                        backgroundColor: 'var(--mark-danger-surface)',
                        border: '1px solid #f5c6cb',
                        borderRadius: '0.4rem',
                        padding: '0.6rem 0.8rem',
                        marginBottom: '1rem'
                    }}
                >
                    {entryError}
                </P>
            )}
            {isEntry && (
                <span
                    style={{
                        display: 'inline-block',
                        padding: '0.3rem 0.8rem',
                        borderRadius: '0.4rem',
                        backgroundColor: 'var(--mark-success-surface)',
                        color: 'var(--mark-success)',
                        marginBottom: '1rem'
                    }}
                >
                    Entry step
                </span>
            )}
            {step.type === 'check_segment' && (
                <CheckSegmentForm
                    step={step as CheckSegmentStep}
                    allSteps={allSteps}
                    onChange={(patch) => onChange(step.id, patch)}
                    dripTargetKind={dripTargetKind}
                />
            )}
            {step.type === 'activity' && (
                <ActivityForm
                    step={step as ActivityStep}
                    allSteps={allSteps}
                    onChange={(patch) => onChange(step.id, patch)}
                    onEditTemplate={onEditTemplate ? () => onEditTemplate(step.id) : undefined}
                    onSaveAndEditTemplate={
                        onSaveAndEditTemplate ? () => onSaveAndEditTemplate(step.id) : undefined
                    }
                    saveAndEditTemplateDisabled={saveAndEditTemplateDisabled}
                    sendWindowErrors={sendWindowErrors}
                    channelLocked={hasSavedTemplate}
                    channelLockedReason={
                        hasSavedTemplate
                            ? "This step's channel is locked — it may already have a saved template, and the template row can never be re-tagged to a different channel."
                            : undefined
                    }
                    canEditTemplate={canEditTemplate}
                    isPersisted={(persistedStepIds?.has(step.id) ?? false) && !unsavedChannelChange}
                    unsavedChannelChange={unsavedChannelChange}
                />
            )}
            {step.type === 'ab_split' && (
                <AbSplitForm
                    step={step as AbSplitStep}
                    allSteps={allSteps}
                    onChange={(patch) => onChange(step.id, patch)}
                />
            )}
            <div className={styles.inspectorFooter}>
                <MarkButton type="button" compact tone="danger" onClick={() => onDelete(step.id)}>
                    Delete step
                </MarkButton>
                <MarkButton compact onClick={onClose}>
                    Done
                </MarkButton>
            </div>
        </MarkModal>
    );
};
export default StepConfigPanel;
